/**
 * SHELL — tiny typed DOM helpers.
 *
 * A zero-dependency micro-toolkit so screens read declaratively without a
 * framework. Everything is strictly typed; no innerHTML with interpolated data
 * (we set textContent), so there is no XSS surface from user/leaderboard strings.
 */

type Falsy = false | null | undefined;
export type Child = Node | string | number | Falsy;

interface ElProps {
  readonly class?: string;
  readonly id?: string;
  readonly text?: string | number;
  readonly html?: string;
  readonly title?: string;
  readonly role?: string;
  readonly href?: string;
  readonly type?: string;
  readonly disabled?: boolean;
  readonly hidden?: boolean;
  readonly ariaLabel?: string;
  readonly ariaLive?: 'off' | 'polite' | 'assertive';
  readonly tabIndex?: number;
  readonly dataset?: Readonly<Record<string, string>>;
  readonly style?: Readonly<Partial<CSSStyleDeclaration>>;
  readonly attrs?: Readonly<Record<string, string>>;
  readonly on?: Readonly<Partial<{ [K in keyof HTMLElementEventMap]: (ev: HTMLElementEventMap[K]) => void }>>;
}

function append(el: Node, children: readonly Child[]): void {
  for (const c of children) {
    if (c === false || c === null || c === undefined) continue;
    el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

/** Create an element with typed props and children. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.class) node.className = props.class;
  if (props.id) node.id = props.id;
  if (props.text !== undefined) node.textContent = String(props.text);
  if (props.html !== undefined) node.innerHTML = props.html; // only for trusted static markup (icons)
  if (props.title !== undefined) node.title = props.title;
  if (props.role) node.setAttribute('role', props.role);
  if (props.ariaLabel) node.setAttribute('aria-label', props.ariaLabel);
  if (props.ariaLive) node.setAttribute('aria-live', props.ariaLive);
  if (props.tabIndex !== undefined) node.tabIndex = props.tabIndex;
  if (props.hidden) node.hidden = true;
  if (props.href !== undefined && node instanceof HTMLAnchorElement) node.href = props.href;
  if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
    if (props.type) node.type = props.type as typeof node.type;
    if (props.disabled) node.disabled = true;
  }
  if (props.dataset) for (const [k, v] of Object.entries(props.dataset)) node.dataset[k] = v;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) node.setAttribute(k, v);
  if (props.style) Object.assign(node.style, props.style);
  if (props.on) {
    for (const [name, handler] of Object.entries(props.on)) {
      if (handler) node.addEventListener(name, handler as EventListener);
    }
  }
  append(node, children);
  return node;
}

/** Shorthand for a styled <div>. */
export function div(cls: string, ...children: Child[]): HTMLDivElement {
  return el('div', { class: cls }, ...children);
}

/** A button with a click handler. */
export function button(
  cls: string,
  label: Child,
  onClick: (ev: MouseEvent) => void,
  extra: Omit<ElProps, 'class' | 'on'> = {},
): HTMLButtonElement {
  return el('button', { class: cls, type: 'button', ...extra, on: { click: onClick } }, label);
}

/** Remove all children of a node. */
export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Replace a node's children with new ones. */
export function replaceChildren(node: Node, ...children: Child[]): void {
  clear(node);
  append(node, children);
}

/** Query a single element, throwing if missing (used for must-exist mounts). */
export function mustQuery<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Expected element matching "${selector}"`);
  return found;
}

/** requestAnimationFrame wrapped in a promise — for transitions/next-paint. */
export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}
